import { useState } from "react";
import { APIError, requestJSON } from "../api";
import { useI18n } from "../i18n";
import { ManagementDialog, ManagementMessages } from "../managementUI";
import { createOperationId } from "../operationId";
import { readPendingRefund, savePendingRefund, clearPendingRefund } from "./pendingRefund";

type Charge = { id: string; team_id: string; balance_delta: string; refunded_points?: string | null; refundable_points?: string | null };
export function RefundDialog({ charge, administratorID, csrfToken, onClose, onRefunded }: {
  charge: Charge; administratorID: string; csrfToken: string; onClose: () => void; onRefunded: () => void;
}) {
  const { t } = useI18n();
  const storageKey = `pacture.refund.${administratorID}.${charge.team_id}.${charge.id}`;
  const [recovery] = useState(() => {try {return {command:readPendingRefund(storageKey), error:null as unknown}} catch(error) {return {command:null, error}}});
  const restored = recovery.command;
  const [points, setPoints] = useState(restored?.points ?? "");
  const [reason, setReason] = useState(restored?.reason ?? "");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [pending, setPending] = useState<{ id: string; points: string; reason: string } | null>(restored);
  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (busy || done || recovery.error) return;
    const command = pending ?? { id: createOperationId(), points, reason: reason.trim() };
    setBusy(true); setError(null);
    try {
      savePendingRefund(storageKey, command);
      setPending(command);
      await requestJSON("/api/v1/billing/refund", {
        method: "POST", csrfToken, reason: command.reason,
        body: { id: command.id, team_id: charge.team_id, reference_id: charge.id, points: command.points },
      });
      clearPendingRefund(storageKey);
      setPending(null); setDone(true); onRefunded();
    } catch (cause) {
      setError(cause);
      // A definite rejection can be corrected; uncertain responses retain the exact command.
      if (cause instanceof APIError && [400, 402, 404, 409, 422].includes(cause.status)) {
        clearPendingRefund(storageKey); setPending(null);
      }
    } finally { setBusy(false); }
  }
  const locked = busy || pending !== null;
  return <ManagementDialog title={t("退还点数", "Refund points")} eyebrow="POINTS REFUND" onClose={onClose} disabled={busy}>
    <p>{t("团队", "Team")}: <strong>{charge.team_id}</strong></p>
    <p>{t("原扣点流水", "Original charge")}: <code>{charge.id}</code></p>
    <p>{t("原扣点金额", "Original charge amount")}: {charge.balance_delta.replace(/^-/, "")}</p>
    <p className="monitor-note">{t("仅退还本次消费的内部点数，不涉及支付渠道退款。累计退款不能超过原扣点金额，已退款金额由系统在提交时核验。", "Returns internal points for this charge, without a payment-provider refund. The server checks previous refunds and the original charge limit on submission.")}</p>
    <p>{t("累计已退 / 剩余可退（提交时再次核验）", "Refunded / refundable (rechecked on submission)")}: {charge.refunded_points ?? t("未知", "Unknown")} / {charge.refundable_points ?? t("未知", "Unknown")}</p>
    <form className="monitor-form" onSubmit={submit}>
      <label>{t("退款点数", "Points to refund")}<input required inputMode="decimal" pattern="(0|[1-9][0-9]*)(\.[0-9]{1,6})?" value={points} disabled={locked || done} onChange={event => setPoints(event.target.value)} /></label>
      <label>{t("退款原因", "Refund reason")}<input required maxLength={512} value={reason} disabled={locked || done} onChange={event => setReason(event.target.value)} /></label>
      <ManagementMessages error={error || recovery.error} />
      {done ? <p role="status">{t("退款已记录，正在刷新账本。", "Refund recorded; refreshing the ledger.")}</p> : null}
      {pending && !busy ? <p role="status">{t("结果尚未确认。可关闭窗口，重新打开后重试原退款；本标签页刷新后仍保留原操作标识。", "Outcome unconfirmed. You may close and reopen this dialog to retry the original refund. Its operation ID survives reloads in this tab.")}</p> : null}
      <div className="dialog-actions">
        <button type="button" className="secondary-button" disabled={busy} onClick={onClose}>{t("关闭", "Close")}</button>
        {!done ? <button className="primary-button" disabled={busy || !!recovery.error || !reason.trim() || !points}>{busy ? t("提交中…", "Submitting…") : pending ? t("重试原退款", "Retry original refund") : t("确认退款", "Confirm refund")}</button> : null}
      </div>
    </form>
  </ManagementDialog>;
}
